import React, { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import MessageScreen from './MessageScreen';
import StarRatingScreen from './star-rating/StarRatingScreen';
import ReviewLinksScreen from './review-links/ReviewLinksScreen';
import { update, submitStarRating, trackProviderClick } from '@/api';
import { theme, media } from '@/styles/theme';
import type { AppProps, InitializedWidgetData } from '@/types';
import {
  useWidgetVariant,
  WidgetVariant,
} from '@/context/WidgetVariantContext';

// Styled components
const Container = styled.div`
  padding: 12px;

  ${media.tablet} {
    padding: 25px;
  }
`;

const InnerContainer = styled.div<{
  $borderColor: string;
  widgetVariant: WidgetVariant;
}>`
  container-type: inline-size;
  container-name: review-widget;
  min-height: 375px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 0 18px 0
    ${({ theme, widgetVariant }) =>
      widgetVariant === 'embed' ? theme.colors.shadow : 'none'};
  border-radius: ${({ theme }) => theme.borderRadius};
  border-top: 8px solid ${({ $borderColor }) => $borderColor};
  border-bottom: 8px solid ${({ $borderColor }) => $borderColor};

  ${({ widgetVariant }) =>
    widgetVariant === 'embed'
      ? `
    height: 375px;
    overflow-x: hidden;
    overflow-y: auto;
  `
      : `
    height: auto;
    overflow: hidden;
  `}

  ${media.tablet} {
    box-shadow: 0 0 35px 0 ${({ theme }) => theme.colors.shadowTablet};
  }
`;

const Content = styled.div`
  position: relative;
  flex-grow: 1;
  padding: 14px;
  display: flex;
  flex-direction: column;
  min-width: 0;

  ${media.mobileLandscape} {
    padding: 24px;
  }
`;

// Accumulated state type
interface AccumulatedState {
  rating: number | null;
  providerId: number | null;
  message: string | null;
}

// Standalone action functions that preserve accumulated state
const handleStarRating = async (
  rating: number,
  initialData: InitializedWidgetData,
  accumulatedState: AccumulatedState,
  updateAccumulatedState: (newState: Partial<AccumulatedState>) => void
) => {
  const isDemoMode =
    initialData.mode === 'demo' || initialData.mode === 'preview';

  // Update accumulated state
  updateAccumulatedState({ rating });

  if (isDemoMode) {
    console.log('🌟 Demo Mode - Star Rating:', {
      rating,
      reminder: initialData.reminder,
      timestamp: new Date().toISOString(),
    });
  } else if (initialData.reminder && initialData.site !== 0) {
    try {
      await submitStarRating(
        rating,
        accumulatedState.message,
        initialData.reminder,
        {
          reminder: initialData.reminder,
          event: initialData.event,
          message: accumulatedState.message,
          person: initialData.person,
          provider: accumulatedState.providerId,
          rating,
          site: initialData.site,
        }
      );
    } catch (error) {
      console.error('Failed to submit star rating:', error);
    }
  }
};

const handleProviderClick = async (
  providerId: number,
  providerSlug: string,
  initialData: InitializedWidgetData,
  accumulatedState: AccumulatedState,
  updateAccumulatedState: (newState: Partial<AccumulatedState>) => void
) => {
  const isDemoMode =
    initialData.mode === 'demo' || initialData.mode === 'preview';

  // Update accumulated state
  updateAccumulatedState({ providerId });

  if (isDemoMode) {
    console.log('🔗 Demo Mode - Provider Click:', {
      provider: providerSlug,
      providerId,
      reminder: initialData.reminder,
      timestamp: new Date().toISOString(),
    });
  } else if (initialData.reminder && initialData.site !== 0) {
    try {
      await trackProviderClick(providerSlug, initialData.reminder, {
        reminder: initialData.reminder,
        event: initialData.event,
        message: accumulatedState.message,
        person: initialData.person,
        provider: providerId,
        rating: accumulatedState.rating,
        site: initialData.site,
      });
    } catch (error) {
      console.error('Failed to track provider click:', error);
    }
  }
};

const handleSendMessage = async (
  message: string,
  initialData: InitializedWidgetData,
  accumulatedState: AccumulatedState,
  updateAccumulatedState: (newState: Partial<AccumulatedState>) => void
) => {
  const isDemoMode =
    initialData.mode === 'demo' || initialData.mode === 'preview';

  // Update accumulated state
  updateAccumulatedState({ message });

  if (isDemoMode) {
    console.log('💬 Demo Mode - Message Sent:', {
      message,
      reminder: initialData.reminder,
      timestamp: new Date().toISOString(),
    });
  } else if (initialData.reminder && initialData.site !== 0) {
    try {
      await update({
        reminder: initialData.reminder,
        event: initialData.event,
        message,
        person: initialData.person,
        provider: accumulatedState.providerId,
        rating: accumulatedState.rating,
        site: initialData.site,
      });
    } catch (error) {
      console.error('Failed to update widget data:', error);
    }
  }
};

const App: React.FC<AppProps> = ({ initialData }) => {
  // Track interactive state
  const [currentRating, setCurrentRating] = useState<number | null>(
    initialData.ratingClicked || null
  );

  const mode = useWidgetVariant();

  // Track accumulated state for API calls
  const [accumulatedState, setAccumulatedState] = useState<AccumulatedState>({
    rating: initialData.ratingClicked || null,
    providerId: initialData.provider || null,
    message: initialData.message || null,
  });

  const updateAccumulatedState = (newState: Partial<AccumulatedState>) => {
    setAccumulatedState((prev) => ({ ...prev, ...newState }));
  };

  const setStarRating = async (rating: number) => {
    setCurrentRating(rating);
    await handleStarRating(
      rating,
      initialData,
      accumulatedState,
      updateAccumulatedState
    );
  };

  const resetStarRating = () => {
    setCurrentRating(null);
    updateAccumulatedState({ rating: null });
  };

  const addReviewLinkClick = async (
    providerId: number,
    providerSlug: string
  ) => {
    await handleProviderClick(
      providerId,
      providerSlug,
      initialData,
      accumulatedState,
      updateAccumulatedState
    );
  };

  const sendMessage = async (message: string) => {
    await handleSendMessage(
      message,
      initialData,
      accumulatedState,
      updateAccumulatedState
    );
  };

  const { theme: widgetTheme, rating, links, site } = initialData;

  const happy = currentRating ? currentRating >= rating.stars_cutoff : false;
  const formOnly =
    Boolean(currentRating) && !happy && Boolean(rating.unhappy_form_only);

  /** Customizations for Chicago Crime Tours */
  if (site === 195) {
    console.warn('Chicago Crime Tours Customized Review Widget by TOMIS');
    return (
      <ThemeProvider theme={theme}>
        <Container>
          <InnerContainer $borderColor={widgetTheme} widgetVariant={mode}>
            <Content>
              <MessageScreen
                rating={currentRating || 0}
                primaryColor={widgetTheme}
                resetStarRating={resetStarRating}
                message={
                  rating.unhappy_message ||
                  "If we didn't meet your expectations, please contact us below."
                }
                sendMessage={sendMessage}
                happy={false}
                formOnly={formOnly}
                site={site}
              >
                <ReviewLinksScreen
                  reviewLinks={links}
                  addReviewLinkClick={addReviewLinkClick}
                  primaryColor={widgetTheme}
                />
              </MessageScreen>
            </Content>
          </InnerContainer>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container>
        <InnerContainer $borderColor={widgetTheme} widgetVariant={mode}>
          <Content>
            {currentRating ? (
              <MessageScreen
                rating={currentRating}
                primaryColor={widgetTheme}
                resetStarRating={resetStarRating}
                message={happy ? rating.happy_message : rating.unhappy_message}
                sendMessage={sendMessage}
                happy={happy}
                formOnly={formOnly}
                site={site}
              >
                <ReviewLinksScreen
                  reviewLinks={links}
                  addReviewLinkClick={addReviewLinkClick}
                  primaryColor={widgetTheme}
                />
              </MessageScreen>
            ) : (
              <StarRatingScreen
                primaryColor={widgetTheme}
                setStarRating={setStarRating}
              />
            )}
          </Content>
        </InnerContainer>
      </Container>
    </ThemeProvider>
  );
};

export default App;
